import { useQueryClient } from '@tanstack/react-query';
import {
  useListEmployeesApiV1PeopleEmployeesGet,
  useGetMyEmployeeApiV1PeopleEmployeesMeGet,
  useGetEmployeeApiV1PeopleEmployeesEmployeeIdGet,
  useCreateEmployeeApiV1PeopleEmployeesPost,
  useUpdateEmployeeApiV1PeopleEmployeesEmployeeIdPut,
  useGetEmployeeRelatedCountsApiV1PeopleEmployeesEmployeeIdRelatedCountsGet,
  getListEmployeesApiV1PeopleEmployeesGetQueryKey,
  getGetEmployeeApiV1PeopleEmployeesEmployeeIdGetQueryKey,
  getGetEmployeeRelatedCountsApiV1PeopleEmployeesEmployeeIdRelatedCountsGetQueryKey,
} from '../../../generated/api/people/people';
import type { ListEmployeesApiV1PeopleEmployeesGetParams } from '../../../generated/api/models/listEmployeesApiV1PeopleEmployeesGetParams';

export function useEmployees(
  params?: ListEmployeesApiV1PeopleEmployeesGetParams,
  // `enabled` so a caller that only needs the roster while a dialog is open can
  // skip the request entirely rather than paying for it on every page load.
  options?: { enabled?: boolean },
) {
  return useListEmployeesApiV1PeopleEmployeesGet(params, {
    query: { enabled: options?.enabled ?? true },
  });
}

export function useEmployee(employeeId: string) {
  return useGetEmployeeApiV1PeopleEmployeesEmployeeIdGet(employeeId, {
    query: { enabled: !!employeeId },
  });
}

/**
 * The signed-in user's own employee record — the id every self-service read is
 * keyed by (leave balances, own requests, own onboarding checklist).
 *
 * 404 is an expected answer, not a failure: plenty of users (contractors' admins,
 * sysadmins, anyone not on the payroll) have no employee record, and the caller
 * renders an explanatory empty state for that. So retries are off — retrying a
 * 404 three times only delays that state by a second.
 */
export function useMyEmployee() {
  return useGetMyEmployeeApiV1PeopleEmployeesMeGet({ query: { retry: false } });
}

/**
 * Per-facet record tallies for the employee-detail tab strip's content dots, in
 * ONE request instead of one list request per tab.
 *
 * `time` is module-admin only and is absent from the payload otherwise (not
 * zero) — see the backend route. An absent key reads as "no dot", which is the
 * correct rendering for a caller who may not know whether entries exist.
 *
 * Documents are deliberately not here: those records belong to file_management,
 * so that tab keeps its own `useFiles` query.
 */
export function useEmployeeRelatedCounts(employeeId: string) {
  return useGetEmployeeRelatedCountsApiV1PeopleEmployeesEmployeeIdRelatedCountsGet(employeeId, {
    query: { enabled: !!employeeId },
  });
}

/**
 * Query key for the tallies above. Facet mutations invalidate it so a dot
 * appears the moment the first record of that kind is created — without it the
 * strip would keep claiming a tab is empty while the tab shows the new row.
 */
export const employeeRelatedCountsQueryKey =
  getGetEmployeeRelatedCountsApiV1PeopleEmployeesEmployeeIdRelatedCountsGetQueryKey;

/**
 * Matches EVERY employee's related-counts query regardless of employee id — for
 * a mutation that changes counts across many employees at once (launching a
 * review cycle bulk-creates one review per active employee), where there is no
 * single employeeId to key an invalidation on.
 *
 * The generated key's first element is the *interpolated* URL, so it can only be
 * matched by shape; a key built from a placeholder id would match nothing.
 */
export function isEmployeeRelatedCountsQuery(queryKey: readonly unknown[]): boolean {
  const [url] = queryKey;
  return (
    typeof url === 'string' &&
    url.startsWith('/api/v1/people/employees/') &&
    url.endsWith('/related-counts')
  );
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useCreateEmployeeApiV1PeopleEmployeesPost({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListEmployeesApiV1PeopleEmployeesGetQueryKey(),
        });
      },
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useUpdateEmployeeApiV1PeopleEmployeesEmployeeIdPut({
    mutation: {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: getListEmployeesApiV1PeopleEmployeesGetQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getGetEmployeeApiV1PeopleEmployeesEmployeeIdGetQueryKey(variables.employeeId),
        });
      },
    },
  });
}

// `useTerminateEmployee` lives in ./useOffboarding — terminating is the entry
// point to the offboarding transition (it also revokes account access and
// applies the exit checklist), so its cache invalidation belongs beside the rest
// of that lifecycle rather than with the plain employee CRUD hooks.

